import { useSyncExternalStore } from "react";
import { endExports } from "@/lib/exportCharge";

// The daily-limit gate, in one place. Replaces a design where every click
// sent its own usage.check request and only acted when the answer came back
// -- measured by QA at 539 ms to over 6 s per click, with no busy state, and
// with a late answer able to overwrite text typed after the click. The
// server's answer is now cached (and kept in localStorage), a click decides
// from the cache immediately, and usage is logged in the background.
//
// What the cache does and does not decide:
//  - "allowed" in the cache: act immediately, count the use locally right
//    away (so the banner and the next click are correct instantly), and
//    confirm with the server in the background.
//  - "blocked" in the cache: the one case that waits for the server, because
//    a purchase or a new day changes the answer and a wrong block is worse
//    than a short wait.
//  - nothing known at all (first visit, empty storage): wait once for the
//    server, never longer than a couple of seconds.
//  - server unreachable: fall back to the last count kept in localStorage,
//    so going offline no longer removes the limit.

export interface UsageSnapshot {
  allowed: boolean;
  usageToday: number;
  limit: number;
  isPro: boolean;
  isAdmin: boolean;
}

export interface ScopedSnapshot extends UsageSnapshot {
  /** Local calendar day this was recorded on; a stored count from another day is ignored. */
  day: string;
  /** Who it's for: "auth" for a signed-in account, "dev:<id>" for an anonymous device. */
  scope: string;
}

export type UsageFetcher = () => Promise<UsageSnapshot>;
export type UseDecision = "allowed" | "blocked";

const STORAGE_KEY = "fdp-usage-v1";
const STALE_MS = 60_000;
const FIRST_ANSWER_TIMEOUT_MS = 2000;
/** Longest a click waits for the server before deciding on what is already known. A hung request must never leave a button doing nothing. */
const CONFIRM_TIMEOUT_MS = 4000;

let current: ScopedSnapshot | null = null;
let fetchedAt = 0;
let inflight: { scope: string; promise: Promise<boolean> } | null = null;
let pendingLogs = 0;
const listeners = new Set<() => void>();

export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** The cached count is kept per tier: Premium Applications have their own daily count and limit. */
export function usageScope(isAuthenticated: boolean, deviceId: string | undefined, premium = false): string {
  const base = isAuthenticated ? "auth" : `dev:${deviceId ?? "unknown"}`;
  return premium ? `${base}:premium` : base;
}

function emit() {
  Array.from(listeners).forEach((l) => l());
}

function persist() {
  try {
    if (current) localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable (private mode, quota) -- the in-memory cache still works */
  }
}

function readPersisted(scope: string): ScopedSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && p.scope === scope && p.day === todayKey() && typeof p.usageToday === "number" && typeof p.limit === "number") {
      return p as ScopedSnapshot;
    }
  } catch {
    /* corrupt entry -- treat as absent */
  }
  return null;
}

function setSnapshot(scope: string, s: UsageSnapshot) {
  current = { ...s, day: todayKey(), scope };
  fetchedAt = Date.now();
  persist();
  emit();
}

/** The snapshot for this scope if there is a valid one for today, else null. */
export function peekUsage(scope: string): ScopedSnapshot | null {
  return current && current.scope === scope && current.day === todayKey() ? current : null;
}

/** Loads a stored snapshot for this scope into memory if nothing better is there yet. */
export function hydrateUsage(scope: string) {
  if (peekUsage(scope)) return;
  const stored = readPersisted(scope);
  if (stored) {
    current = stored;
    fetchedAt = 0; // provisional: usable immediately, but still worth refreshing
    emit();
  }
}

export function subscribeUsage(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding: re-renders when the cached usage changes. */
export function useUsageSnapshot(): ScopedSnapshot | null {
  return useSyncExternalStore(subscribeUsage, () => current, () => null);
}

/** Asks the server and stores the answer. Resolves true if the server answered. */
export function refreshUsage(scope: string, fetcher: UsageFetcher): Promise<boolean> {
  if (inflight && inflight.scope === scope) return inflight.promise;
  const promise = (async () => {
    try {
      const fresh = await fetcher();
      const local = peekUsage(scope);
      if (pendingLogs > 0 && local) {
        // Uses were just counted locally and their logs haven't all landed
        // yet; the server's number can lag behind. Never move backwards.
        const usageToday = Math.max(fresh.usageToday, local.usageToday);
        setSnapshot(scope, { ...fresh, usageToday, allowed: fresh.isPro || fresh.isAdmin || usageToday < fresh.limit });
      } else {
        setSnapshot(scope, fresh);
      }
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
    }
  })();
  inflight = { scope, promise };
  return promise;
}

/** Like refreshUsage, but never waits longer than `ms`: a slow or hung server leaves the last known count in place. */
export async function refreshUsageBounded(scope: string, fetcher: UsageFetcher, ms = CONFIRM_TIMEOUT_MS): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([refreshUsage(scope, fetcher), new Promise((resolve) => { timer = setTimeout(resolve, ms); })]);
  if (timer) clearTimeout(timer);
}

/**
 * When the cached count says this is the LAST trial use (or none is left), confirm with the server before a file
 * leaves, so a stale cache (another tab, another device, a day pass that ended) cannot hand out an extra file at
 * the edge of the limit. Far from the limit nothing waits.
 */
export async function confirmAtEdge(scope: string, fetcher: UsageFetcher): Promise<void> {
  const s = peekUsage(scope);
  if (!s || s.isPro || s.isAdmin) return;
  if (s.limit - s.usageToday <= 1) await refreshUsageBounded(scope, fetcher);
}

/** Takes a usage answer some other part of the app already fetched (the
 * banner's own query), applying the same never-go-backwards rule. */
export function acceptServerUsage(scope: string, fresh: UsageSnapshot) {
  const local = peekUsage(scope);
  if (pendingLogs > 0 && local) {
    const usageToday = Math.max(fresh.usageToday, local.usageToday);
    setSnapshot(scope, { ...fresh, usageToday, allowed: fresh.isPro || fresh.isAdmin || usageToday < fresh.limit });
  } else {
    setSnapshot(scope, fresh);
  }
}

/** Fetches in the background if nothing is known or what's known is stale. */
export function ensureUsage(scope: string, fetcher: UsageFetcher) {
  hydrateUsage(scope);
  if (!peekUsage(scope) || Date.now() - fetchedAt > STALE_MS) void refreshUsage(scope, fetcher);
}

/** Counts a use immediately in the cache, ahead of the server's own record of it. */
export function recordLocalUse(scope: string) {
  const s = peekUsage(scope);
  if (!s) return;
  const usageToday = s.usageToday + 1;
  current = { ...s, usageToday, allowed: s.isPro || s.isAdmin || usageToday < s.limit };
  persist();
  emit();
}

export function beginLog() {
  pendingLogs++;
}

/** Call when a background usage log has settled (success or failure). */
export function endLog(scope: string, fetcher: UsageFetcher, onRefreshed?: () => void) {
  pendingLogs = Math.max(0, pendingLogs - 1);
  if (pendingLogs === 0) void refreshUsage(scope, fetcher).then(() => onRefreshed?.());
}

/**
 * The decision. `onAllowed` runs when a use is granted -- it's where the
 * caller starts its background log.
 */
export async function requestUse(scope: string, fetcher: UsageFetcher, onAllowed: () => void): Promise<UseDecision> {
  hydrateUsage(scope);
  let snap = peekUsage(scope);

  if (!snap) {
    // Nothing known at all: wait for the first answer, but never long.
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      refreshUsage(scope, fetcher),
      new Promise((resolve) => {
        timer = setTimeout(resolve, FIRST_ANSWER_TIMEOUT_MS);
      }),
    ]);
    if (timer) clearTimeout(timer);
    snap = peekUsage(scope);
    if (!snap) {
      // Server unreachable and nothing stored: the only case left that fails
      // open (there is no count to enforce against).
      onAllowed();
      return "allowed";
    }
  }

  if (snap.allowed) {
    recordLocalUse(scope);
    onAllowed();
    return "allowed";
  }

  // Cached as blocked: confirm with the server before turning anyone away --
  // an upgrade or a new day changes the answer. If the server can't be
  // reached, the stored count stands and the block holds.
  await refreshUsageBounded(scope, fetcher);
  const fresh = peekUsage(scope);
  if (fresh?.allowed) {
    recordLocalUse(scope);
    onAllowed();
    return "allowed";
  }
  return "blocked";
}

/**
 * Asks whether a use WOULD be allowed, without taking one. A bundle uses this at Start so someone who is out
 * of uses is stopped up front, while the use itself is taken later, when the first step produces a result.
 */
export async function canStartUse(scope: string, fetcher: UsageFetcher): Promise<boolean> {
  hydrateUsage(scope);
  let snap = peekUsage(scope);
  if (!snap) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([refreshUsage(scope, fetcher), new Promise((r) => { timer = setTimeout(r, FIRST_ANSWER_TIMEOUT_MS); })]);
    if (timer) clearTimeout(timer);
    snap = peekUsage(scope);
    if (!snap) return true; // nothing known and the server is unreachable: same fail-open as requestUse
  }
  if (snap.allowed) return true;
  await refreshUsageBounded(scope, fetcher); // an upgrade or a new day changes the answer
  return !!peekUsage(scope)?.allowed;
}

// One tool session = one use.
//
// A "tool session" is one visit to one tool's page. The first thing that spends a use in it (a file drop,
// the first result of a live tool, a download or a button action) counts one use; everything else the
// person does in that same visit with that tool is part of the same job and costs nothing more. Before this,
// a file tool counted once for the file drop and again for the download, and every click of an action
// button counted again. The session ends when the person leaves the tool's page (ToolPage calls
// endToolSession on unmount); coming back later is a new session and counts again.
const countedSessions = new Set<string>();
const pendingSessions = new Map<string, Promise<UseDecision>>();

export function isToolSessionCounted(toolId: string): boolean {
  return countedSessions.has(toolId);
}

/** Told whenever a tool visit spends its use (first time per visit), so analytics can send tool_completed from one place. */
let spentListener: ((toolId: string) => void) | null = null;
export function onToolUseSpent(fn: ((toolId: string) => void) | null) {
  spentListener = fn;
}
function notifySpent(toolId: string) {
  try { spentListener?.(toolId); } catch { /* telemetry must never break a tool */ }
}

export function markToolSessionCounted(toolId: string) {
  if (!countedSessions.has(toolId)) notifySpent(toolId);
  countedSessions.add(toolId);
}

export function endToolSession(toolId: string) {
  endExports(toolId);
  countedSessions.delete(toolId);
  pendingSessions.delete(toolId);
}

/**
 * The decision for one tool session: "allowed" at no cost once this session has counted; otherwise asks
 * the daily-limit gate once (concurrent callers in the same session share that one decision).
 */
export function requestToolSessionUse(toolId: string, scope: string, fetcher: UsageFetcher, onAllowed: () => void): Promise<UseDecision> {
  if (countedSessions.has(toolId)) return Promise.resolve("allowed");
  const pending = pendingSessions.get(toolId);
  if (pending) return pending;
  const p = requestUse(scope, fetcher, onAllowed).then((d) => {
    if (d === "allowed") { if (!countedSessions.has(toolId)) notifySpent(toolId); countedSessions.add(toolId); }
    pendingSessions.delete(toolId);
    return d;
  });
  pendingSessions.set(toolId, p);
  return p;
}

/** A further spend in a visit that had already counted (an export whose content changed): also a completed run. */
export function noteExtraUseSpent(toolId: string) {
  notifySpent(toolId);
}

/** Test helper: forget everything held in memory. */
export function __resetUsageGateForTests() {
  spentListener = null;
  current = null;
  fetchedAt = 0;
  inflight = null;
  pendingLogs = 0;
  listeners.clear();
  countedSessions.clear();
  pendingSessions.clear();
}

/** The last known usage snapshot, whatever its scope (null before the first answer). Used to decide on the trial mark. */
export function getCurrentUsage(): ScopedSnapshot | null {
  return current && current.day === todayKey() ? current : null;
}
